"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Analytics } from "@vercel/analytics/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { useEffect } from "react";
import { useAutoCartSync } from "@/hooks/use-cart-sync";
import { queryClient } from "@/utils/orpc";
import { AuthProvider } from "./auth/auth-provider";
import { ThemeProvider } from "./theme-provider";
import { Toaster } from "./ui/sonner";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Constants for Lenis/GSAP integration
const GSAP_TIME_SCALE = 1000; // Convert GSAP time (seconds) to milliseconds

function CartSyncProvider({ children }: { children: React.ReactNode }) {
  // Enable auto cart sync when commerce is enabled
  useAutoCartSync(true);

  return <>{children}</>;
}

function LenisProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Initialize Lenis with autoRaf disabled to avoid duplicate RAF loops
    const lenis = new Lenis({ autoRaf: false });

    // Update GSAP's ScrollTrigger when Lenis scrolls
    lenis.on("scroll", ScrollTrigger.update);

    // Store ticker function for cleanup
    const tickerFunction = (time: number) => {
      lenis.raf(time * GSAP_TIME_SCALE); // GSAP time is in seconds
    };

    // GSAP's ticker is the single source of truth for driving Lenis
    gsap.ticker.add(tickerFunction);
    gsap.ticker.lagSmoothing(0);

    // Store lenis instance globally for access in components
    if (typeof window !== "undefined") {
      (window as { lenis?: Lenis }).lenis = lenis;
    }

    return () => {
      lenis.destroy();
      gsap.ticker.remove(tickerFunction);
    };
  }, []);

  return <>{children}</>;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <CartSyncProvider>
              <LenisProvider>
                {children}
                <Analytics />
              </LenisProvider>
            </CartSyncProvider>
            <ReactQueryDevtools />
          </NuqsAdapter>
        </QueryClientProvider>
      </AuthProvider>
      <Toaster richColors />
    </ThemeProvider>
  );
}
